import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { LoadingButton } from '@mui/lab';
import { Box, Grid, MenuItem, TextField } from '@mui/material';
import { toast } from 'react-toastify';
import { useRef } from 'react';
import {
  checkInStudent,
  type CheckInPayload,
  type PickupType,
} from '../api/students';

const schema = yup.object({
  first_name: yup.string().required('First name is required'),
  last_name: yup.string().required('Last name is required'),
  grade: yup.string().required('Grade is required'),
  pickup_type: yup
    .mixed<PickupType>()
    .oneOf(['car', 'foot'], 'Pickup type must be car or walker')
    .required('Pickup type is required'),
});

type FormValues = yup.InferType<typeof schema>;

type StudentFormProps = {
  onSuccess: () => void | Promise<unknown>;
};

export default function StudentForm({ onSuccess }: StudentFormProps) {
  const firstNameRef = useRef<HTMLInputElement>(null);
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      first_name: '',
      last_name: '',
      grade: 'K',
      pickup_type: 'car',
    },
  });

  const onSubmit = async (data: CheckInPayload) => {
    try {
      console.debug('[StudentForm] Submitting check-in', data);
      await checkInStudent(data);
      toast.success('Student checked-in');
      reset();
      await onSuccess();
      console.debug('[StudentForm] Check-in complete');
      // Return focus to first name field for quick sequential entries
      setTimeout(() => firstNameRef.current?.focus(), 0);
    } catch (e) {
      console.error('[StudentForm] Check-in failed', e);
      toast.error('Failed to check-in student');
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Controller
            name="first_name"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                inputRef={firstNameRef}
                label="First name"
                fullWidth
                autoFocus
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message ?? ' '}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Controller
            name="last_name"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Last name"
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message ?? ' '}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Controller
            name="grade"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                select
                label="Grade"
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message ?? ' '}
              >
                <MenuItem value="Pre-K">Pre-K</MenuItem>
                <MenuItem value="K">Kindergarten</MenuItem>
                <MenuItem value="1">1st Grade</MenuItem>
                <MenuItem value="2">2nd Grade</MenuItem>
                <MenuItem value="3">3rd Grade</MenuItem>
                <MenuItem value="4">4th Grade</MenuItem>
                <MenuItem value="5">5th Grade</MenuItem>
                <MenuItem value="6">6th Grade</MenuItem>
                <MenuItem value="7">7th Grade</MenuItem>
                <MenuItem value="8">8th Grade</MenuItem>
              </TextField>
            )}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Controller
            name="pickup_type"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                select
                label="Going home by"
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message ?? ' '}
              >
                <MenuItem value="car">Car</MenuItem>
                <MenuItem value="foot">Walker</MenuItem>
              </TextField>
            )}
          />
        </Grid>
      </Grid>

      <Grid container justifyContent="flex-end" mt={1.5}>
        <LoadingButton
          type="submit"
          loading={isSubmitting}
          variant="contained"
          sx={{ mt: 1 }}
        >
          Check-in
        </LoadingButton>
      </Grid>
    </Box>
  );
}
